import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useContent } from "@/hooks/useContent";
import PageSEO from "@/components/PageSEO";

const FUNDS = [
  { k: "general", t: "General Fund", d: "Wherever it is most needed." },
  { k: "scholarships", t: "Scholarships", d: "Sponsor a healer or student." },
  { k: "food_forest", t: "Food Forest", d: "Trees, seeds, water, hands." },
  { k: "construction", t: "Construction", d: "Bamboo, roofs, and gathering halls." },
  { k: "land_expansion", t: "Land Expansion", d: "Adding acres to the sanctuary." },
];

const AMOUNTS = [25, 50, 100, 250, 500];

// v1 CONNECTORS SPRINT · Fractured Atlas fiscal-sponsorship panel.
// NEOS does NOT process this payment. Click is logged; user is
// redirected to the Founder-approved Fractured Atlas URL.
function FracturedAtlasSupport() {
  const [state, setState] = useState({ loading: true, configured: false, url: null });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/support-url");
        setState({ loading: false, configured: !!data.configured, url: data.url });
      } catch {
        setState({ loading: false, configured: false, url: null });
      }
    })();
  }, []);

  const handleClick = async () => {
    try {
      await api.post("/public/donate-click", { source: "public_donate_page", context: "fractured_atlas_panel" });
    } catch {}
    if (state.url) window.open(state.url, "_blank", "noopener,noreferrer");
    else toast.info("The Fractured Atlas donation link is not yet configured. Please try again shortly.");
  };

  return (
    <div className="rounded-2xl border border-terracotta/40 bg-terracotta/5 p-6 mb-8" data-testid="fractured-atlas-panel">
      <div className="text-xs uppercase tracking-widest text-terracotta/80 mb-1">Fiscal Sponsor · Fractured Atlas</div>
      <h3 className="text-2xl font-serif text-ink mb-2">Support Celestial Seed via Fractured Atlas</h3>
      <p className="text-sm text-ink-soft mb-4">
        Fractured Atlas is our fiscal sponsor. Your tax-deductible contribution goes directly to Celestial Seed through their platform. NEOS does not process this payment.
      </p>
      <button
        type="button"
        onClick={handleClick}
        disabled={state.loading}
        data-testid="fractured-atlas-donate-btn"
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-terracotta text-bone hover:bg-ink transition disabled:opacity-50"
      >
        {state.loading ? "Loading…" : state.configured ? "Donate via Fractured Atlas →" : "Fractured Atlas link pending"}
      </button>
      {!state.configured && !state.loading && (
        <div className="text-xs text-ink-soft italic mt-2" data-testid="fa-not-configured">
          The Founder-approved Fractured Atlas URL has not yet been added to NEOS. Click is still logged for institutional records.
        </div>
      )}
    </div>
  );
}

export default function Donate() {
  const { t } = useContent("donate");
  const [fund, setFund] = useState("general");
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [monthly, setMonthly] = useState(false);
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const finalAmount = Number(custom || amount);
    if (!finalAmount || finalAmount < 1) { toast.error("Enter an amount of at least $1"); setBusy(false); return; }
    try {
      const { data } = await api.post("/donate", {
        amount: finalAmount, fund, monthly,
        donor_name: donor.name || undefined,
        donor_email: donor.email || undefined,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div>
      <PageSEO
        title={"Donate | Support Celestial Seed Sanctuary"}
        description={"Support Celestial Seed Sanctuary. Fund arts residencies, ecological restoration, volunteer programs, and long-term institutional development."}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-12">
        <div className="overline mb-6" data-testid="donate-overline">{t("hero.overline", "Donate")}</div>
        <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" data-testid="donate-title">
          {t("hero.title", "Every dollar becomes a tree, a meal, a scholarship, a roof.")}
        </h1>
      </section>
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        <FracturedAtlasSupport />
        <form onSubmit={submit} className="bg-surface border border-line rounded-2xl p-8 space-y-8">
          <div>
            <div className="overline mb-3">Choose a fund</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {FUNDS.map(f => (
                <button type="button" key={f.k} onClick={() => setFund(f.k)} data-testid={`donate-fund-${f.k}`}
                  className={`text-left rounded-xl border p-4 transition-colors duration-200 ${fund === f.k ? "border-forest bg-forest text-bone" : "border-line bg-bone text-ink hover:border-forest"}`}>
                  <div className="font-serif text-xl">{f.t}</div>
                  <div className={`text-sm mt-1 ${fund === f.k ? "text-bone/80" : "text-ink-soft"}`}>{f.d}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-3">Amount (USD)</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {AMOUNTS.map(a => (
                <button type="button" key={a} onClick={() => { setAmount(a); setCustom(""); }} data-testid={`donate-amt-${a}`}
                  className={`px-5 py-2 rounded-full text-sm border transition-colors duration-200 ${!custom && amount === a ? "bg-terracotta text-bone border-terracotta" : "bg-bone border-line hover:border-terracotta"}`}>${a}</button>
              ))}
            </div>
            <Input type="number" min="1" step="1" placeholder="Custom amount" data-testid="donate-custom" value={custom} onChange={e=>setCustom(e.target.value)} />
          </div>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={monthly} onChange={e=>setMonthly(e.target.checked)} data-testid="donate-monthly" className="w-4 h-4 accent-forest" />
            <span className="text-ink">Make this a monthly gift</span>
          </label>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label className="overline">Name (optional)</Label><Input data-testid="donate-name" value={donor.name} onChange={e=>setDonor({...donor, name: e.target.value})} /></div>
            <div><Label className="overline">Email (optional)</Label><Input type="email" data-testid="donate-email" value={donor.email} onChange={e=>setDonor({...donor, email: e.target.value})} /></div>
          </div>
          <button type="submit" disabled={busy} data-testid="donate-submit" className="pill pill-accent w-full justify-center">
            {busy ? "Preparing checkout…" : `Give ${monthly ? "monthly" : "once"}`}
          </button>
          <p className="text-xs text-ink-soft">Secure payment by Stripe. Test card: 4242 4242 4242 4242.</p>
        </form>
      </section>
    </div>
  );
}
